import { roundNumberToDecimal } from '@monetize/utils/core';
import { FormulaEvaluationError, FormulaFunction } from './evaluator';

/**
 * Builtin scalar function registry for formulas.
 *
 * The formula language itself has no loops, so every formula is guaranteed
 * to terminate.
 *
 * Numeric arguments are permissive to match how template data arrives:
 * numeric strings are coerced, and null/undefined degrade to NaN rather than
 * throwing — a NaN result renders as blank instead of failing the template.
 */

const toNumber = (value: unknown, fnName: string): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (value === undefined || value === null) {
    return NaN;
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? NaN : Number(value);
  }
  throw new FormulaEvaluationError(
    `${fnName}() expects number arguments, received ${typeof value}`,
  );
};

const toNumberOrDefault = (
  value: unknown,
  fnName: string,
  defaultValue: number,
): number => (value === undefined ? defaultValue : toNumber(value, fnName));

export const FORMULA_BUILTIN_FUNCTIONS: Record<string, FormulaFunction> = {
  min: (...args) => Math.min(...args.map((arg) => toNumber(arg, 'min'))),
  max: (...args) => Math.max(...args.map((arg) => toNumber(arg, 'max'))),
  abs: (value) => Math.abs(toNumber(value, 'abs')),
  floor: (value) => Math.floor(toNumber(value, 'floor')),
  ceil: (value) => Math.ceil(toNumber(value, 'ceil')),
  sqrt: (value) => Math.sqrt(toNumber(value, 'sqrt')),
  pow: (base, exponent) =>
    Math.pow(toNumber(base, 'pow'), toNumber(exponent, 'pow')),
  ln: (value) => Math.log(toNumber(value, 'ln')),
  log10: (value) => Math.log10(toNumber(value, 'log10')),
  round: (value, decimals) =>
    roundNumberToDecimal(
      toNumber(value, 'round'),
      toNumberOrDefault(decimals, 'round', 0),
    ),
  clamp: (value, lower, upper) =>
    Math.min(
      Math.max(toNumber(value, 'clamp'), toNumber(lower, 'clamp')),
      toNumber(upper, 'clamp'),
    ),
  /** First argument that is not null/undefined/NaN, else undefined */
  coalesce: (...args) =>
    args.find(
      (arg) =>
        arg !== null &&
        arg !== undefined &&
        !(typeof arg === 'number' && isNaN(arg)),
    ),
};

export const FORMULA_BUILTIN_FUNCTION_NAMES: ReadonlySet<string> = new Set(
  Object.keys(FORMULA_BUILTIN_FUNCTIONS),
);
